import { useState, useEffect, useCallback } from "react";
import { PlusCircle, Trash2, Pencil, X } from "lucide-react";
import { mcpResourcesService, type MCPResourceRow } from "./mcpResourcesService";

// ── data ───────────────────────────────────────────────────────────
const CATEGORIES = ["Database", "API", "Storage", "Agents", "Workflows", "Other"];

type ResType = "tool" | "resource" | "prompt";

// ── MCPResources ────────────────────────────────────────────────────
export default function MCPResources() {
  const [showForm, setShowForm] = useState(false);
  const [resources, setResources] = useState<MCPResourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [resType, setResType]   = useState<ResType>("tool");
  const [resCat, setResCat]     = useState(CATEGORIES[0]);
  const [resId, setResId]       = useState("");
  const [resName, setResName]   = useState("");
  const [resDesc, setResDesc]   = useState("");
  const [resIsActive, setResIsActive] = useState(true);
  const [flash, setFlash]       = useState(false);

  // confirm toggle (Is Active) before applying
  const [confirmToggle, setConfirmToggle] = useState<{ id: string; newValue: boolean } | null>(null);
  // confirm delete (assignedTokenCount set when opening to show warning)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string; assignedTokenCount: number } | null>(null);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await mcpResourcesService.getAll();
      setResources(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load resources");
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const handleSubmit = async () => {
    if (!resId?.trim() || !resName?.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await mcpResourcesService.register({
        resourceId: resId.trim(),
        name: resName.trim(),
        category: resCat,
        type: resType,
        description: resDesc.trim() || undefined,
        isActive: resIsActive,
      });
      setResources(p => [created, ...p]);
      setResId(""); setResName(""); setResDesc(""); setResIsActive(true);
      setFlash(true);
      setTimeout(() => { setFlash(false); setShowForm(false); }, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to register resource");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await mcpResourcesService.delete(id);
      setResources(p => p.filter(r => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete resource");
    }
  };

  const applyToggleResourceActive = async () => {
    if (!confirmToggle) return;
    try {
      const updated = await mcpResourcesService.update(confirmToggle.id, { isActive: confirmToggle.newValue });
      if (updated) {
        setResources(p => p.map(r => r.id === confirmToggle.id ? { ...r, isActive: updated.isActive } : r));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update resource");
    }
    setConfirmToggle(null);
  };

  return (
    <div className="p-6 space-y-6 text-sm text-gray-300">

      {/* ── header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">MCP Resources</h2>
          <p className="text-sm text-gray-400">Manage all registered MCP tools, resources, and prompts.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm shadow transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Register New Resource
        </button>
      </div>

      {error && (
        <div className="rounded border border-red-800 bg-red-900/30 px-4 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {/* ── registration form popup ───────────────────────────────── */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !flash && setShowForm(false)}
        >
          <div
            className="bg-neutral-900 border border-neutral-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-white text-sm">Register MCP Resource</h3>
                <p className="text-xs text-gray-400 mt-0.5">Define a tool, resource, or prompt that the MCP server exposes to developers</p>
              </div>
              <button
                type="button"
                onClick={() => !flash && setShowForm(false)}
                className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              <div className="space-y-5">

                {/* Step 1: Type */}
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-2">Resource Type</p>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { type: "tool" as ResType,     icon: "⚙",  label: "Tool",     desc: "Executable with side effects" },
                      { type: "resource" as ResType, icon: "◈",  label: "Resource", desc: "Read-only data providers" },
                      { type: "prompt" as ResType,   icon: "◎",  label: "Prompt",   desc: "Reusable prompt templates" },
                    ]).map(t => (
                      <button key={t.type} type="button" onClick={() => setResType(t.type)}
                        className={`p-3 rounded border text-center transition-colors ${
                          resType === t.type
                            ? "border-indigo-500 bg-indigo-900/30 text-indigo-300"
                            : "border-neutral-700 bg-neutral-800/50 text-gray-400 hover:border-neutral-600"
                        }`}
                      >
                        <div className="text-lg mb-1">{t.icon}</div>
                        <div className="text-xs font-semibold">{t.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Identity */}
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-2">Identity & Description</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Resource Identifier *</label>
                      <input className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 font-mono placeholder:text-gray-600 outline-none focus:border-indigo-500 transition-colors"
                        placeholder="e.g. db/query_execute" value={resId} onChange={e => setResId(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Display Name *</label>
                      <input className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 outline-none focus:border-indigo-500 transition-colors"
                        placeholder="e.g. Execute SQL Query" value={resName} onChange={e => setResName(e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs text-gray-500 mb-1">Category</label>
                    <select
                      value={resCat}
                      onChange={e => setResCat(e.target.value)}
                      className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 outline-none focus:border-indigo-500 transition-colors"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Description</label>
                  <textarea className="w-full bg-neutral-800 border border-neutral-700 rounded px-3 py-1.5 text-xs text-gray-200 placeholder:text-gray-600 outline-none focus:border-indigo-500 resize-none transition-colors"
                    rows={3} placeholder="What does this resource do?" value={resDesc} onChange={e => setResDesc(e.target.value)} />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-gray-400 font-medium">Is Active</label>
                  <button
                    type="button"
                    onClick={() => setResIsActive(a => !a)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${resIsActive ? "bg-green-600" : "bg-neutral-600"}`}
                    role="switch"
                    aria-checked={resIsActive}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${resIsActive ? "translate-x-5" : "translate-x-0.5"}`}
                      style={{ marginTop: 2 }}
                    />
                  </button>
                  <span className="text-xs text-gray-500">{resIsActive ? "Active" : "Inactive"}</span>
                </div>
              </div> 

              {/* actions */}
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={handleSubmit} disabled={!resId?.trim() || !resName?.trim() || submitting}
                    className={`flex items-center px-4 py-2 rounded text-sm font-medium shadow transition-colors ${
                      flash ? "bg-green-600 text-white"
                        : (!resId?.trim() || !resName?.trim() || submitting) ? "bg-indigo-900/40 text-indigo-500 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white"
                    }`}
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    {flash ? "✓ Registered!" : submitting ? "Registering…" : "Register Resource"}
                  </button>
                  <button type="button" onClick={() => { setResId(""); setResName(""); setResDesc(""); setResIsActive(true); }}
                    className="px-4 py-2 rounded text-sm font-medium border border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600 transition-colors">
                    Clear
                  </button>
                  <button type="button" onClick={() => !flash && setShowForm(false)}
                    className="px-4 py-2 rounded text-sm font-medium border border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── resources table ──────────────────────────────────────── */}
      <div className="overflow-x-auto border border-neutral-800 rounded">
        {loading ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading resources…</div>
        ) : (
        <table className="min-w-full text-sm text-left text-gray-300">
          <thead className="bg-neutral-900 text-gray-400">
            <tr>
              {["Resource ID", "Name", "Category", "Type", "Created On", "Is Active", "Actions"].map(h => (
                <th key={h} className="px-4 py-2 text-xs font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resources.map((r) => (
              <tr key={r.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                <td className="px-4 py-3 font-mono text-xs text-indigo-400">{r.resourceId}</td>
                <td className="px-4 py-3 text-gray-200">{r.name}</td>
                <td className="px-4 py-3 text-gray-400">{r.cat}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-neutral-700 text-gray-300">
                    {r.type}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">
                  {new Date(r.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setConfirmToggle({ id: r.id, newValue: !r.isActive })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-neutral-900 ${r.isActive ? "bg-green-600" : "bg-neutral-600"}`}
                    role="switch"
                    aria-checked={r.isActive}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${r.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                      style={{ marginTop: 2 }}
                    />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button className="text-yellow-400 hover:text-white transition-colors" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="text-red-400 hover:text-red-200 transition-colors" title="Delete"
                      onClick={async () => {
                        try {
                          const { assignedTokenCount } = await mcpResourcesService.getAssignedTokenCount(r.id);
                          setDeleteConfirm({ id: r.id, name: r.name, assignedTokenCount });
                        } catch {
                          setDeleteConfirm({ id: r.id, name: r.name, assignedTokenCount: 0 });
                        }
                      }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Confirm Is Active toggle */}
      {confirmToggle !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl max-w-sm w-full p-5">
            <h4 className="text-sm font-semibold text-white mb-1">
              {confirmToggle.newValue ? "Activate resource" : "Deactivate resource"}
            </h4>
            <p className="text-xs text-gray-400 mb-4">
              {confirmToggle.newValue
                ? "This resource will be active and available to developers."
                : "This resource will be inactive and unavailable until activated again."}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmToggle(null)}
                className="px-3 py-1.5 rounded text-xs font-medium border border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyToggleResourceActive}
                className={`px-3 py-1.5 rounded text-xs font-medium text-white transition-colors ${confirmToggle.newValue ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {confirmToggle.newValue ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl max-w-sm w-full p-5">
            <h4 className="text-sm font-semibold text-white mb-1">Delete resource</h4>
            <p className="text-xs text-gray-400 mb-2">
              Are you sure you want to delete <span className="text-gray-200 font-medium">&quot;{deleteConfirm.name}&quot;</span>? This action cannot be undone.
            </p>
            {deleteConfirm.assignedTokenCount > 0 && (
              <p className="text-xs text-amber-300 bg-amber-900/30 border border-amber-700/50 rounded px-3 py-2 mb-4">
                This resource is assigned to <strong>{deleteConfirm.assignedTokenCount}</strong> developer token(s). Deleting will not revoke those tokens; they will reference a removed resource.
              </p>
            )}
            {deleteConfirm.assignedTokenCount === 0 && <div className="mb-4" />}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-1.5 rounded text-xs font-medium border border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!deleteConfirm) return;
                  await handleDelete(deleteConfirm.id);
                  setDeleteConfirm(null);
                }}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}